import { VALUE_DETECTORS } from './patterns';
import { scrubUrl } from './scrub-url';

export const FILTERED = '[Filtered]';

/**
 * Keys whose VALUE is always sensitive regardless of the value's shape. Normalized
 * (lowercased, underscores/hyphens removed) before comparison.
 */
const PII_KEYS = new Set([
  'email',
  'phone',
  'phonenumber',
  'password',
  'token',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'cookie',
  'secret',
  'apikey',
  'firstname',
  'lastname',
  'fullname',
  'name',
  'dateofbirth',
  'address',
  'iban',
  'vat',
  'ssn',
  // Subject identifiers. The rule is "no user id at all" (BAL-7806), and these reach
  // Sentry outside `event.user` — e.g. pino-sentry-transport writes its configured
  // `context` keys into `contexts['pino-context']`, which `event.user = {}` never touches.
  'userid',
  'uid',
  'sub',
  'customerid',
  'businessid',
  'enduserid',
]);

const normalizeKey = (key: string) => key.toLowerCase().replaceAll('_', '').replaceAll('-', '');

/** Whether a (normalized) key names a URL, so its value should be route-templated rather than filtered. */
const isUrlKey = (normalizedKey: string) =>
  normalizedKey === 'url' || normalizedKey.endsWith('url');

/** Redact every detector match inside a single string. */
export const redactString = (value: string): string => {
  let result = value;

  for (const detector of VALUE_DETECTORS) {
    result = result.replace(detector, FILTERED);
  }

  return result;
};

/**
 * Recursively redact a value: filters strings, recurses into arrays/objects, and
 * blanks any value whose key is a known-sensitive name. URL-named keys are
 * route-templated instead of filtered so `/reports/:id/summary` survives.
 */
export const redactValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return redactString(value);
  }

  if (Array.isArray(value)) {
    return value.map(item => redactValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => {
        const normalized = normalizeKey(key);

        if (PII_KEYS.has(normalized)) {
          return [key, FILTERED];
        }

        if (isUrlKey(normalized) && typeof entry === 'string') {
          return [key, scrubUrl(entry)];
        }

        return [key, redactValue(entry)];
      }),
    );
  }

  return value;
};
